/**
 * WorkforcePulseTab.js — Phase 2: fetches from GET /api/hr/workforce/summary
 */
import React, { useState, useEffect } from 'react';
import { Grid, Card, CardContent, Typography, Box, CircularProgress, Alert } from '@mui/material';
import { LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { PeopleAlt, TrendingDown, Diversity3, ShowChart } from '@mui/icons-material';
import MetricCard from '../components/MetricCard';
import SimulationPanel from '../components/SimulationPanel';
import AttritionHeatmap from '../components/AttritionHeatmap';
import { workforceApi } from '../services/hrmsApi';
import { useChatContext } from '../context/ChatContext';

const DEPT_COLORS = ['#1565C0', '#00897B', '#F57F17', '#AD1457', '#6A1B9A'];
const colorMap = {
  Male:         "#1565C0",
  Female:       "#E91E8C",
  "Non-binary": "#9C27B0",
};

// ── Shared pie-label constants (fixes a, d) ───────────────────────────────────
const RADIAN           = Math.PI / 180;
const LEGEND_FONT_SIZE = 13;
const LABEL_FONT_SIZE  = 12;

// Renders "XX%" in the segment's own colour; skips slices < 5% (fix a)
const renderColoredPercentLabel = ({ cx, cy, midAngle, outerRadius, value, fill, percent }) => {
  if (percent < 0.01) return null;
  const radius = outerRadius + 12;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill={fill} textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="auto" fontSize={LABEL_FONT_SIZE} fontWeight={600}>
      {`${value}%`}
    </text>
  );
};

export default function WorkforcePulseTab() {
  const { openWithMessage } = useChatContext();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    workforceApi.getSummary()
      .then(setData)
      .catch((e) => setError(e.response?.data?.message || 'Failed to load workforce data.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  if (error)   return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;

  const { totalHeadcount, attritionRatePercent, attritionTrendLabel, attritionTrendData, diversityData, headcountGrowthData, topHiringDept, topHiringDeptNet } = data;
  const depts = headcountGrowthData?.length > 0 ? Object.keys(headcountGrowthData[0]).filter(k => k !== 'month') : [];

  // ── Dynamic MetricCard insight / status ──────────────────────────────────────
  const hcTotals = (headcountGrowthData || []).map(r => depts.reduce((sum, d) => sum + (Number(r[d]) || 0), 0));
  const hcDelta  = hcTotals.length >= 2 ? hcTotals[hcTotals.length - 1] - hcTotals[hcTotals.length - 2] : null;
  const headcountStatus  = hcDelta == null ? 'blue' : hcDelta > 0 ? 'green' : hcDelta === 0 ? 'yellow' : 'red';
  const headcountInsight = hcDelta == null   ? '🔵 Headcount trend unavailable'
    : hcDelta > 0   ? `🟢 Growing — up ${hcDelta} from last month`
    : hcDelta === 0 ? '🟡 Headcount stable vs last month'
    : `🔴 Declining — down ${Math.abs(hcDelta)} from last month`;

  const attritionStatus  = attritionRatePercent == null ? 'blue'
    : attritionRatePercent < 3 ? 'green'
    : attritionRatePercent < 5 ? 'yellow'
    : 'red';
  const attritionInsight = attritionRatePercent == null ? '🔵 Attrition data unavailable'
    : attritionRatePercent < 3 ? '🟢 Healthy — attrition within safe range'
    : attritionRatePercent < 5 ? '🟡 Moderate — monitor trend closely'
    : '🔴 High attrition — immediate action required';

  const malePctNum   = typeof data.malePct   === 'string' ? parseFloat(data.malePct)   : data.malePct;
  const femalePctNum = typeof data.femalePct  === 'string' ? parseFloat(data.femalePct) : data.femalePct;
  const genderGap    = malePctNum != null && femalePctNum != null ? Math.abs(malePctNum - femalePctNum) : null;
  const underRep     = malePctNum != null && femalePctNum != null && malePctNum > femalePctNum ? 'female' : 'male';
  const diversityStatus  = genderGap == null ? 'blue' : genderGap <= 10 ? 'green' : genderGap <= 25 ? 'yellow' : 'red';
  const diversityInsight = genderGap == null ? '🔵 Diversity data unavailable'
    : genderGap <= 10 ? '🟢 Balanced gender ratio'
    : genderGap <= 25 ? `🟡 Gender gap — increase ${underRep} recruitment`
    : `🔴 Significant imbalance — EDI intervention needed`;

  const hiringStatus  = topHiringDept == null ? 'blue' : topHiringDeptNet >= 10 ? 'green' : 'blue';
  const hiringInsight = topHiringDept == null ? '🔵 No hiring data this quarter'
    : `🔵 ${topHiringDept} leading headcount expansion (+${topHiringDeptNet})`;

  return (
    <Grid container spacing={2.5}>
      <Grid item xs={12} sm={6} lg={3}>
        <MetricCard title="Total Headcount" value={totalHeadcount?.toLocaleString() || '—'} subtitle="Active Employees" icon={PeopleAlt} iconColor="#1565C0" trend={attritionTrendLabel} insight={headcountInsight} status={headcountStatus}
          onAskAI={() => openWithMessage(`Current headcount is ${totalHeadcount}. What are the key drivers of recent headcount changes and what trends should management watch?`)} />
      </Grid>
      <Grid item xs={12} sm={6} lg={3}>
        <MetricCard title="Attrition Rate" value={`${attritionRatePercent?.toFixed(1)}%`} subtitle="Current Month" icon={TrendingDown} iconColor="#C62828" trend={attritionTrendLabel} insight={attritionInsight} status={attritionStatus}
          onAskAI={() => openWithMessage(`Monthly attrition rate is ${attritionRatePercent?.toFixed(1)}%. What is causing this and what actions can reduce attrition?`)} />
      </Grid>
      <Grid item xs={12} sm={6} lg={3}>
        <MetricCard title="Diversity Index" value={data.malePct && data.femalePct ? `${data.malePct} / ${data.femalePct}` : '—'} subtitle="Male / Female split" icon={Diversity3} iconColor="#6A1B9A" insight={diversityInsight} status={diversityStatus}
          onAskAI={() => openWithMessage(`Our gender diversity is ${data.malePct} male / ${data.femalePct} female. What EDI initiatives would be most impactful to improve balance?`)} />
      </Grid>
      <Grid item xs={12} sm={6} lg={3}>
        <MetricCard title="Top Hiring Dept." value={topHiringDept || '—'} subtitle={topHiringDeptNet ? `Net +${topHiringDeptNet} this quarter` : 'This quarter'} icon={ShowChart} iconColor="#00897B" insight={hiringInsight} status={hiringStatus}
          onAskAI={() => openWithMessage(`${topHiringDept} is the top hiring department with net +${topHiringDeptNet} this quarter. Is this growth pace sustainable and are there risk factors?`)} />
      </Grid>

      <Grid item xs={12} md={7}>
        <Card sx={{ height: 320 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>Attrition Trend</Typography>
            <Typography variant="caption" color="text.secondary">Monthly turnover %</Typography>
            <ResponsiveContainer width="100%" height={230}>
              <LineChart data={attritionTrendData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip formatter={(v) => v != null ? [`${(+v).toFixed(1)}%`] : ['—']} />
                <Legend />
                <Line type="monotone" dataKey="rate" stroke="#1565C0" strokeWidth={2.5} dot={{ r: 4 }} name="Actual %" connectNulls={false} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </Grid>

      {/*<Grid item xs={12} md={5}>
        <Card sx={{ height: 320 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>Diversity Index</Typography>
            <Typography variant="caption" color="text.secondary">Gender distribution across organisation</Typography>
            <ResponsiveContainer width="100%" height={230}>
              <PieChart>
                <Pie data={diversityData} cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={3} dataKey="value" label={({ name, value }) => `${name}: ${value}%`}>
                  {(diversityData || []).map((entry) => <Cell key={entry.name} fill={entry.color} />)}
                </Pie>
                <Tooltip formatter={(v) => [`${v}%`]} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </Grid>*/}

      <Grid item xs={12} md={5}>
        <Card sx={{ height: 320 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              Diversity Index
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Gender distribution across organisation
            </Typography>
            <ResponsiveContainer width="100%" height={230}>
              <PieChart>
                <Pie
                  data={diversityData}
                  cx="50%"
                  cy="46%"
                  innerRadius={40}
                  outerRadius={90}
                  paddingAngle={3}
                  dataKey="value"
                  startAngle={90}
                  endAngle={-270}
                  labelLine={false}
                  label={renderColoredPercentLabel}
                >
                  {(diversityData || []).map((entry) => {
                    const fill = colorMap[entry.name] ?? entry.color;
                    return <Cell key={entry.name} fill={fill} />;
                  })}
                </Pie>
                <Tooltip formatter={(v) => [`${v}%`]} />
                <Legend
                  verticalAlign="bottom"
                  formatter={(value) => (
                    <span style={{ fontSize: LEGEND_FONT_SIZE, color: '#555' }}>{value}</span>
                  )}
                />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card sx={{ height: 320 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>Headcount Growth by Department</Typography>
            <Typography variant="caption" color="text.secondary">Line chart — monthly employee count</Typography>
            <ResponsiveContainer width="100%" height={235}>
              <LineChart data={headcountGrowthData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <defs>
                  {depts.map((d, i) => (
                    <linearGradient key={d} id={`grad${d}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={DEPT_COLORS[i % DEPT_COLORS.length]} stopOpacity={0.7} />
                      <stop offset="95%" stopColor={DEPT_COLORS[i % DEPT_COLORS.length]} stopOpacity={0.1} />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} domain={[0, 500]} ticks={[0, 100, 200, 300, 400, 500]} />
                <Tooltip /><Legend />
                {depts.map((d, i) => (
                  <Line key={d} type="monotone" dataKey={d} stroke={DEPT_COLORS[i % DEPT_COLORS.length]} strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <AttritionHeatmap />
      </Grid>

      <Grid item xs={12}>
        <SimulationPanel />
      </Grid>
    </Grid>
  );
}
